import { Tooltip } from '@carbon/react';

import { Link } from 'components/link';
import type { InterestWorkspaceResponse } from 'types/api-responses';
import styles from './reconciliation-summary.module.scss';
import { WorkspaceDescriptionEditor } from './workspace-description-editor';

interface ReconciliationSummaryProps {
  workspace: InterestWorkspaceResponse;
}

/**
 * Reconciliation summary meta block rendered directly below the page header.
 *
 * Inset card containing a single-line meta strip (`Summary · Entity: {link}`)
 * at productive-heading-03 size. Below the strip: the workspace description,
 * wrapped and clamped to three lines with a tooltip carrying the full text,
 * and an inline pencil to edit it (the pencil is hidden once promoted;
 * when there's no description yet, the row shows an "Add a description"
 * affordance unless promoted). The instructional paragraph follows.
 *
 * @param {ReconciliationSummaryProps} props - Component props.
 * @param {InterestWorkspaceResponse} props.workspace - The loaded workspace
 *   record. Drives the entity link and the description display/editor.
 * @return {JSX.Element} The rendered reconciliation summary section.
 */
export const ReconciliationSummary = ({
  workspace,
}: ReconciliationSummaryProps): JSX.Element => (
  <section className={styles.section}>
    <div className={styles.titleRow}>
      <h2 className={styles.title}>Summary</h2>
      <span className={styles.sep} aria-hidden="true">
        ·
      </span>
      <span className={styles.metaItem}>
        <span className={styles.metaLabel} aria-hidden="true">
          Entity:
        </span>
        <Link
          className={styles.metaLink}
          to={`/entities/${workspace.entityId}`}
          openInNewTab
          aria-label={`Entity: ${workspace.entityName}`}
        >
          {workspace.entityName}
        </Link>
      </span>
    </div>
    {(workspace.description || !workspace.promoted) && (
      <p className={styles.workspaceDescription}>
        <span className={styles.descriptionLabel}>Description:</span>{' '}
        {workspace.description ? (
          <Tooltip
            className={styles.descriptionTooltip}
            label={workspace.description}
            align="bottom"
            autoAlign
          >
            <span className={styles.descriptionText} tabIndex={0}>
              {workspace.description}
            </span>
          </Tooltip>
        ) : (
          <span className={styles.descriptionEmpty}>Add a description</span>
        )}
        <WorkspaceDescriptionEditor workspace={workspace} />
      </p>
    )}
    <p className={styles.description}>
      Select one or more slots using the checkboxes, then click{' '}
      <strong>Certify Interest</strong> to choose the correct NRI for each
      tract. Once the tracts are certified, click <strong>Promote</strong> to
      publish the certified values.
    </p>
  </section>
);
